import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

function getStored(): Theme {
  const stored = localStorage.getItem('theme') as Theme | null;
  if (stored) return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

// Global event name — fired whenever any hook instance toggles theme
const THEME_EVENT = 'cf-theme-change';

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getStored);

  useEffect(() => {
    // Sync when another instance of useTheme toggles
    const handler = () => setTheme(getStored());
    window.addEventListener(THEME_EVENT, handler);
    return () => window.removeEventListener(THEME_EVENT, handler);
  }, []);

  const toggle = () => {
    const next: Theme = getStored() === 'dark' ? 'light' : 'dark';
    localStorage.setItem('theme', next);
    applyTheme(next);
    setTheme(next);
    // Notify all other useTheme instances
    window.dispatchEvent(new Event(THEME_EVENT));
  };

  return { theme, toggle };
}

// Apply theme immediately on load (before React hydrates) to avoid flash
const _stored = localStorage.getItem('theme');
const _prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
if (_stored === 'dark' || (!_stored && _prefersDark)) {
  document.documentElement.classList.add('dark');
}
